import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CURRENCY_LIST } from '../../constants/serviceTypes';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Users } from 'lucide-react';

const MANPOWER_TYPES = [
  { value: 'forklift_operator', label: 'Forklift Operator' },
  { value: 'warehouse_worker', label: 'Warehouse Worker' },
  { value: 'loader', label: 'Loader / Unloader' },
  { value: 'picker_packer', label: 'Picker / Packer' },
  { value: 'inventory_clerk', label: 'Inventory Clerk' },
  { value: 'supervisor', label: 'Warehouse Supervisor' },
  { value: 'driver', label: 'Driver (LMV)' },
  { value: 'heavy_driver', label: 'Heavy Vehicle Driver' },
  { value: 'crane_operator', label: 'Crane Operator' },
  { value: 'reach_truck_operator', label: 'Reach Truck Operator' },
  { value: 'security', label: 'Security Guard' },
  { value: 'cleaner', label: 'Cleaner' },
  { value: 'general_labor', label: 'General Labor' },
  { value: 'technician', label: 'Maintenance Technician' },
  { value: 'admin', label: 'Administrative Staff' }
];

const MANPOWER_PRICING_MODES = [
  { value: 'hourly', label: 'Hourly Rate' },
  { value: 'daily', label: 'Daily Rate' },
  { value: 'weekly', label: 'Weekly Rate' },
  { value: 'monthly', label: 'Monthly Rate' }
];

const ProviderAddManpower = () => {
  const [formData, setFormData] = useState({
    manpower_type: '',
    name: '',
    description: '',
    workers_available: 1,
    experience_level: 'intermediate',
    certifications: '',
    location_city: '',
    location_state: '',
    location_country: '',
    pricing_mode: 'daily',
    rate: '',
    currency: 'USD',
    available: true
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.manpower_type) {
      toast.error('Please select a manpower type');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/provider/manpower`, {
        ...formData,
        workers_available: parseInt(formData.workers_available) || 1,
        rate: parseFloat(formData.rate) || 0
      });
      toast.success('Manpower service added successfully');
      navigate('/provider/manpower');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add manpower service');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex flex-col">
      <Navbar />
      <div className="flex-1 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-10 w-10 bg-emerald-100 rounded-sm flex items-center justify-center">
              <Users className="h-5 w-5 text-emerald-600" />
            </div>
            <h1 className="font-heading text-3xl font-black text-foreground tracking-tight">
              Add Manpower
            </h1>
          </div>
          <p className="text-muted-foreground">List labor and staffing services</p>
        </div>

        <Card className="bg-white border border-border rounded-sm shadow-sm p-6">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="add-manpower-form">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Worker Type *</Label>
                <Select value={formData.manpower_type} onValueChange={(value) => setFormData({ ...formData, manpower_type: value })}>
                  <SelectTrigger className="rounded-sm" data-testid="manpower-type-select">
                    <SelectValue placeholder="Select worker type..." />
                  </SelectTrigger>
                  <SelectContent>
                    {MANPOWER_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Service Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Skilled Forklift Operators"
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="workers">Workers Available *</Label>
                  <Input
                    id="workers"
                    type="number"
                    min="1"
                    value={formData.workers_available}
                    onChange={(e) => setFormData({ ...formData, workers_available: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Describe the service, skills, experience, etc."
                  rows={3}
                  className="rounded-sm"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Experience Level</Label>
                  <Select value={formData.experience_level} onValueChange={(value) => setFormData({ ...formData, experience_level: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="entry">Entry Level</SelectItem>
                      <SelectItem value="intermediate">Intermediate</SelectItem>
                      <SelectItem value="experienced">Experienced</SelectItem>
                      <SelectItem value="expert">Expert / Specialized</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="certifications">Certifications</Label>
                  <Input
                    id="certifications"
                    value={formData.certifications}
                    onChange={(e) => setFormData({ ...formData, certifications: e.target.value })}
                    placeholder="e.g., Forklift License, Safety Training"
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Service Location</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.location_city}
                    onChange={(e) => setFormData({ ...formData, location_city: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.location_state}
                    onChange={(e) => setFormData({ ...formData, location_state: e.target.value })}
                    className="rounded-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="country">Country *</Label>
                  <Input
                    id="country"
                    value={formData.location_country}
                    onChange={(e) => setFormData({ ...formData, location_country: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-6 space-y-4">
              <h3 className="font-heading text-lg font-bold text-foreground">Pricing</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Pricing Mode</Label>
                  <Select value={formData.pricing_mode} onValueChange={(value) => setFormData({ ...formData, pricing_mode: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MANPOWER_PRICING_MODES.map((mode) => (
                        <SelectItem key={mode.value} value={mode.value}>{mode.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                    <SelectTrigger className="rounded-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CURRENCY_LIST.map((curr) => (
                        <SelectItem key={curr.value} value={curr.value}>{curr.value}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="rate">Rate per Worker *</Label>
                  <Input
                    id="rate"
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.rate}
                    onChange={(e) => setFormData({ ...formData, rate: e.target.value })}
                    required
                    className="rounded-sm"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4 pt-6 border-t border-border">
              <Button
                type="submit"
                disabled={loading}
                className="bg-emerald-600 text-white hover:bg-emerald-700 rounded-sm"
              >
                {loading ? 'Adding...' : (
                  <>
                    <Plus className="mr-2 h-4 w-4" />
                    Add Manpower
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/provider/dashboard')}
                className="rounded-sm"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default ProviderAddManpower;
